import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, safeImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, Lock, LogOut, Loader2, Crown, Star, StarOff, Mail, Phone, Inbox, Landmark, CreditCard, PiggyBank, Plane, BedDouble, Edit3, X } from "lucide-react";

function LoginScreen({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("admin_token", data.token);
      toast.success("Welcome back");
      onAuthed();
    } catch (err) {
      toast.error("Wrong password");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="admin-login-screen">
      <form onSubmit={submit} className="card-tech p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-[#FF0000] flex items-center justify-center">
            <Lock size={18} />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl">Admin Access</h1>
            <p className="text-xs text-[#71717a] font-mono">Restricted area</p>
          </div>
        </div>
        <p className="text-sm text-[#a1a1aa] mt-4 mb-6">
          Enter your admin password to manage settings & affiliate links.
        </p>
        <label className="text-xs text-[#a1a1aa] block mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••"
          className="input-dark"
          autoFocus
          data-testid="admin-password-input"
        />
        <button type="submit" disabled={loading || !password} className="btn-red w-full mt-5 justify-center" data-testid="admin-login-btn">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Lock size={16} />}
          {loading ? "Verifying..." : "Unlock"}
        </button>
        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-[#71717a] hover:text-white transition" data-testid="admin-back-home">
            ← Back to site
          </Link>
        </div>
      </form>
    </div>
  );
}

function AdminPanel({ onLogout }) {
  const [settings, setSettings] = useState({
    creator_name: "",
    creator_bio: "",
    youtube_url: "",
    channel_handle: "",
    channel_id: "",
    youtube_api_key: "",
  });
  const [hasKey, setHasKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newLink, setNewLink] = useState({
    title: "",
    description: "",
    image_url: "",
    target_url: "",
    price: "",
    badge: "",
  });
  const [plans, setPlans] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [financialPartners, setFinancialPartners] = useState([]);
  const [travelPartners, setTravelPartners] = useState([]);
  const [bulkImport, setBulkImport] = useState("");
  const [travelBulkImport, setTravelBulkImport] = useState("");
  const [newFinPartner, setNewFinPartner] = useState({
    category: "banking",
    name: "",
    logo_url: "",
    target_url: "",
    sort_order: 0,
  });
  const [newTravelPartner, setNewTravelPartner] = useState({
    category: "airline",
    name: "",
    logo_url: "",
    target_url: "",
    deal: "",
    tagline: "",
    sort_order: 0,
  });
  const [editingTravel, setEditingTravel] = useState(null); // {id, ...fields}
  const [newPlan, setNewPlan] = useState({
    duration_label: "",
    duration_days: 30,
    price: "",
    features: "",
    popular: false,
    active: true,
    sort_order: 0,
  });

  const load = async () => {
    const [s, a, an, p, inq, fp, tp] = await Promise.all([
      api.get("/settings"),
      api.get("/affiliates"),
      api.get("/analytics/summary"),
      api.get("/subscription-plans?include_inactive=true"),
      api.get("/subscription-inquiries").catch(() => ({ data: [] })),
      api.get("/financial-partners?include_inactive=true"),
      api.get("/travel-partners?include_inactive=true"),
    ]);
    setSettings({ ...s.data, youtube_api_key: "" });
    setHasKey(s.data.youtube_api_key_set);
    setAffiliates(a.data);
    setAnalytics(an.data);
    setPlans(p.data);
    setInquiries(inq.data);
    setFinancialPartners(fp.data);
    setTravelPartners(tp.data);
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — log in again");
        onLogout();
      } else {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const addAffiliate = async () => {
    if (!newLink.title || !newLink.target_url) {
      toast.error("Title and target URL required");
      return;
    }
    try {
      await api.post("/affiliates", newLink);
      setNewLink({ title: "", description: "", image_url: "", target_url: "", price: "", badge: "" });
      toast.success("Affiliate link added");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Failed to add");
    }
  };

  const removeAffiliate = async (id) => {
    if (!window.confirm("Delete this affiliate link?")) return;
    try {
      await api.delete(`/affiliates/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Delete failed");
    }
  };

  const addPlan = async () => {
    if (!newPlan.duration_label || !newPlan.price || !newPlan.duration_days) {
      toast.error("Duration label, days & price are required");
      return;
    }
    try {
      const payload = {
        ...newPlan,
        features: newPlan.features
          .split("\n")
          .map((f) => f.trim())
          .filter(Boolean),
      };
      await api.post("/subscription-plans", payload);
      setNewPlan({ duration_label: "", duration_days: 30, price: "", features: "", popular: false, active: true, sort_order: 0 });
      toast.success("Plan added");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Failed to add plan");
    }
  };

  const updatePlan = async (id, updates) => {
    try {
      await api.put(`/subscription-plans/${id}`, updates);
      toast.success("Plan updated");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Update failed");
    }
  };

  const removePlan = async (id) => {
    if (!window.confirm("Delete this subscription plan?")) return;
    try {
      await api.delete(`/subscription-plans/${id}`);
      toast.success("Plan deleted");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Delete failed");
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.put(`/subscription-inquiries/${id}`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Update failed");
    }
  };

  const removeInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/subscription-inquiries/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Delete failed");
    }
  };

  const addFinPartner = async () => {
    if (!newFinPartner.name || !newFinPartner.logo_url) {
      toast.error("Name and logo URL are required");
      return;
    }
    try {
      await api.post("/financial-partners", newFinPartner);
      setNewFinPartner({ category: newFinPartner.category, name: "", logo_url: "", target_url: "", sort_order: 0 });
      toast.success("Partner added");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Failed to add");
    }
  };

  const removeFinPartner = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    try {
      await api.delete(`/financial-partners/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Delete failed");
    }
  };

  const handleBulkImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(bulkImport);
      if (!Array.isArray(parsed)) throw new Error("Not an array");
    } catch (e) {
      toast.error("Invalid JSON");
      return;
    }
    try {
      const { data } = await api.post("/financial-partners/bulk-import", parsed);
      toast.success(`Imported ${data.inserted} · Skipped ${data.skipped} duplicates`);
      setBulkImport("");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Import failed");
    }
  };

  // ===== Travel Partners handlers =====
  const addTravelPartner = async () => {
    if (!newTravelPartner.name || !newTravelPartner.target_url) {
      toast.error("Name and link URL are required");
      return;
    }
    try {
      await api.post("/travel-partners", newTravelPartner);
      setNewTravelPartner({ ...newTravelPartner, name: "", logo_url: "", target_url: "", deal: "", tagline: "", sort_order: 0 });
      toast.success("Travel partner added");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Failed to add");
    }
  };

  const startEditTravel = (p) => {
    setEditingTravel({ id: p.id, deal: p.deal || "", tagline: p.tagline || "", target_url: p.target_url || "", logo_url: p.logo_url || "", name: p.name || "", sort_order: p.sort_order || 0 });
  };

  const saveTravelEdit = async () => {
    if (!editingTravel?.id) return;
    const { id, ...updates } = editingTravel;
    try {
      await api.put(`/travel-partners/${id}`, updates);
      toast.success("Updated");
      setEditingTravel(null);
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Update failed");
    }
  };

  const removeTravelPartner = async (id) => {
    if (!window.confirm("Delete this travel partner?")) return;
    try {
      await api.delete(`/travel-partners/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Delete failed");
    }
  };

  const handleTravelBulkImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(travelBulkImport);
      if (!Array.isArray(parsed)) throw new Error("Not an array");
    } catch (e) {
      toast.error("Invalid JSON");
      return;
    }
    try {
      const { data } = await api.post("/travel-partners/bulk-import", parsed);
      toast.success(`Imported ${data.inserted} · Skipped ${data.skipped} duplicates`);
      setTravelBulkImport("");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Import failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-page">
      <header className="glass sticky top-0 z-10 px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#a1a1aa] hover:text-white transition" data-testid="admin-back-link">
          <ArrowLeft size={18} /> Back to site
        </Link>
        <h1 className="font-display font-bold text-xl">Admin</h1>
        <button
          onClick={onLogout}
          className="text-xs text-[#71717a] hover:text-[#FF0000] font-mono flex items-center gap-1.5 transition"
          data-testid="admin-logout-btn"
        >
          <LogOut size={14} /> LOGOUT
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-12 space-y-10">
        {analytics && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-analytics">
            {[
              { label: "Total clicks", value: analytics.total_clicks },
              { label: "Affiliate links", value: analytics.total_affiliates },
              { label: "Newsletter signups", value: analytics.newsletter_signups },
              { label: "API key", value: hasKey ? "Set" : "Not set" },
            ].map((s, i) => (
              <div key={i} className="card-tech p-5">
                <div className="text-xs text-[#71717a] uppercase tracking-wider">{s.label}</div>
                <div className="font-display font-extrabold text-2xl mt-2">{s.value}</div>
              </div>
            ))}
          </section>
        )}

        <section className="card-tech p-6" data-testid="admin-settings-section">
          <h2 className="font-display font-bold text-2xl mb-6">Channel settings</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#a1a1aa] block mb-2">Creator name</label>
              <input
                value={settings.creator_name || ""}
                onChange={(e) => setSettings({ ...settings, creator_name: e.target.value })}
                className="input-dark"
                data-testid="settings-creator-name"
              />
            </div>
            <div>
              <label className="text-xs text-[#a1a1aa] block mb-2">YouTube channel URL</label>
              <input
                value={settings.youtube_url || ""}
                onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                placeholder="https://youtube.com/@yourchannel"
                className="input-dark"
                data-testid="settings-yt-url"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[#a1a1aa] block mb-2">Bio</label>
              <textarea
                value={settings.creator_bio || ""}
                onChange={(e) => setSettings({ ...settings, creator_bio: e.target.value })}
                rows={3}
                className="input-dark resize-none"
                data-testid="settings-bio"
              />
            </div>
            <div>
              <label className="text-xs text-[#a1a1aa] block mb-2">YouTube handle (e.g. @mrbeast)</label>
              <input
                value={settings.channel_handle || ""}
                onChange={(e) => setSettings({ ...settings, channel_handle: e.target.value })}
                placeholder="@yourhandle"
                className="input-dark"
                data-testid="settings-handle"
              />
            </div>
            <div>
              <label className="text-xs text-[#a1a1aa] block mb-2">Or channel ID (UC...)</label>
              <input
                value={settings.channel_id || ""}
                onChange={(e) => setSettings({ ...settings, channel_id: e.target.value })}
                placeholder="UCxxxxxxxxxxxxx"
                className="input-dark"
                data-testid="settings-channel-id"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[#a1a1aa] block mb-2">Contact email (shown on subscription cards)</label>
              <input
                type="email"
                value={settings.contact_email || ""}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="you@example.com"
                className="input-dark"
                data-testid="settings-contact-email"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[#a1a1aa] block mb-2">
                YouTube Data API v3 key {hasKey && <span className="text-[#E6FF00]">(currently set — leave blank to keep)</span>}
              </label>
              <input
                type="password"
                value={settings.youtube_api_key || ""}
                onChange={(e) => setSettings({ ...settings, youtube_api_key: e.target.value })}
                placeholder="AIza..."
                className="input-dark"
                data-testid="settings-api-key"
              />
              <p className="text-xs text-[#71717a] mt-2 font-mono">
                Optional. The site already pulls your videos via public RSS — adding a key gives stricter accuracy.
              </p>
            </div>
          </div>
          <button onClick={saveSettings} disabled={saving} className="btn-red mt-6" data-testid="settings-save-btn">
            <Save size={16} /> {saving ? "Saving..." : "Save settings"}
          </button>
        </section>

        <section className="card-tech p-6" data-testid="admin-affiliates-section">
          <h2 className="font-display font-bold text-2xl mb-6">Affiliate links</h2>

          <div className="space-y-3 mb-8">
            {affiliates.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-3 border border-white/10 rounded-lg" data-testid={`admin-affiliate-${a.id}`}>
                <img src={safeImageUrl(a.image_url)} alt="" className="w-12 h-12 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="text-xs text-[#71717a] truncate">{a.target_url}</div>
                </div>
                <div className="text-sm font-mono text-[#E6FF00]">{a.clicks} clicks</div>
                <a href={a.target_url} target="_blank" rel="noreferrer" className="text-[#71717a] hover:text-white">
                  <ExternalLink size={16} />
                </a>
                <button onClick={() => removeAffiliate(a.id)} className="text-[#71717a] hover:text-[#FF0000]" data-testid={`delete-affiliate-${a.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <h3 className="font-display font-semibold text-lg mb-4">Add new link</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input value={newLink.title} onChange={(e) => setNewLink({ ...newLink, title: e.target.value })} placeholder="Title *" className="input-dark" data-testid="new-affiliate-title" />
            <input value={newLink.target_url} onChange={(e) => setNewLink({ ...newLink, target_url: e.target.value })} placeholder="Target URL *" className="input-dark" data-testid="new-affiliate-url" />
            <input value={newLink.image_url} onChange={(e) => setNewLink({ ...newLink, image_url: e.target.value })} placeholder="Image URL" className="input-dark" data-testid="new-affiliate-image" />
            <input value={newLink.price} onChange={(e) => setNewLink({ ...newLink, price: e.target.value })} placeholder="Price (e.g. ₹299)" className="input-dark" data-testid="new-affiliate-price" />
            <input value={newLink.badge} onChange={(e) => setNewLink({ ...newLink, badge: e.target.value })} placeholder="Badge (e.g. MY PICK)" className="input-dark" data-testid="new-affiliate-badge" />
            <input value={newLink.description} onChange={(e) => setNewLink({ ...newLink, description: e.target.value })} placeholder="Description" className="input-dark" data-testid="new-affiliate-desc" />
          </div>
          <button onClick={addAffiliate} className="btn-yellow mt-4" data-testid="add-affiliate-btn">
            <Plus size={16} /> Add link
          </button>
        </section>

        <section className="card-tech p-6" data-testid="admin-financial-section">
          <div className="flex items-center gap-3 mb-2">
            <Landmark size={22} className="text-[#E6FF00]" />
            <h2 className="font-display font-bold text-2xl">Banking, Credit Cards & Loans</h2>
          </div>
          <p className="text-sm text-[#71717a] mb-6">
            Add partner logos in each category. Slot targets: Banking 5 · Credit Cards 7 · Loans 4.
          </p>

          {[
            { key: "banking", label: "Banking Partners", target: 5, icon: Landmark },
            { key: "creditcard", label: "Credit Cards", target: 10, icon: CreditCard },
            { key: "loan", label: "Loans", target: 4, icon: PiggyBank },
            { key: "savings", label: "Zero Balance Savings Account", target: 2, icon: Landmark },
          ].map((cat) => {
            const items = financialPartners.filter((p) => p.category === cat.key);
            return (
              <div key={cat.key} className="mb-8" data-testid={`admin-fin-${cat.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <cat.icon size={18} className="text-[#a1a1aa]" />
                    <h3 className="font-display font-semibold text-lg">{cat.label}</h3>
                    <span className="text-xs font-mono text-[#71717a]">({items.length}/{cat.target})</span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                  {items.length === 0 ? (
                    <p className="text-xs text-[#71717a] col-span-full italic">No partners added yet.</p>
                  ) : (
                    items.map((p) => (
                      <div key={p.id} className="flex items-center gap-3 p-2 border border-white/10 rounded-lg" data-testid={`admin-fin-item-${p.id}`}>
                        <img src={safeImageUrl(p.logo_url)} alt="" className="w-12 h-12 bg-white rounded object-contain p-1" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{p.name}</div>
                          {p.target_url && <div className="text-[10px] text-[#71717a] truncate">{p.target_url}</div>}
                        </div>
                        <button
                          onClick={() => removeFinPartner(p.id)}
                          className="text-[#71717a] hover:text-[#FF0000]"
                          data-testid={`delete-fin-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button
                  onClick={() => setNewFinPartner({ ...newFinPartner, category: cat.key })}
                  className={`text-xs px-3 py-1.5 rounded-md font-mono transition ${
                    newFinPartner.category === cat.key
                      ? "bg-[#E6FF00] text-black"
                      : "bg-white/5 text-[#a1a1aa] hover:bg-white/10"
                  }`}
                  data-testid={`select-fin-cat-${cat.key}`}
                >
                  + Upload to {cat.label}
                </button>
              </div>
            );
          })}

          <div className="border-t border-white/10 pt-6 mt-2">
            <h3 className="font-display font-semibold text-lg mb-1">
              Add new partner to <span className="text-[#E6FF00]">{
                { banking: "Banking Partners", creditcard: "Credit Cards", loan: "Loans", savings: "Zero Balance Savings Account" }[newFinPartner.category]
              }</span>
            </h3>
            <p className="text-xs text-[#71717a] mb-4">
              Upload your logo image via chat, then paste the URL below.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <input
                value={newFinPartner.name}
                onChange={(e) => setNewFinPartner({ ...newFinPartner, name: e.target.value })}
                placeholder="Partner name (e.g. HDFC Bank) *"
                className="input-dark"
                data-testid="new-fin-name"
              />
              <input
                value={newFinPartner.target_url}
                onChange={(e) => setNewFinPartner({ ...newFinPartner, target_url: e.target.value })}
                placeholder="Link URL (optional)"
                className="input-dark"
                data-testid="new-fin-url"
              />
              <input
                value={newFinPartner.logo_url}
                onChange={(e) => setNewFinPartner({ ...newFinPartner, logo_url: e.target.value })}
                placeholder="Logo image URL *"
                className="input-dark md:col-span-2"
                data-testid="new-fin-logo"
              />
              <input
                type="number"
                value={newFinPartner.sort_order}
                onChange={(e) => setNewFinPartner({ ...newFinPartner, sort_order: parseInt(e.target.value, 10) || 0 })}
                placeholder="Sort order"
                className="input-dark"
                data-testid="new-fin-sort"
              />
            </div>
            <button onClick={addFinPartner} className="btn-yellow mt-4" data-testid="add-fin-btn">
              <Plus size={16} /> Add partner
            </button>
          </div>

          <div className="border-t border-white/10 pt-6 mt-8">
            <h3 className="font-display font-semibold text-lg mb-2 text-[#E6FF00]">Bulk import (paste JSON)</h3>
            <p className="text-xs text-[#71717a] mb-3">
              Paste a JSON array to bulk-add banking partners, credit cards, loans, or savings accounts in one shot. Duplicates (same category + name) will be skipped. Handy for migrating from preview → production.
            </p>
            <textarea
              value={bulkImport}
              onChange={(e) => setBulkImport(e.target.value)}
              rows={5}
              className="input-dark resize-none font-mono text-xs"
              placeholder='[{"category":"banking","name":"HDFC Bank","logo_url":"https://...","target_url":"https://..."}]'
              data-testid="bulk-import-textarea"
            />
            <button
              onClick={handleBulkImport}
              disabled={!bulkImport.trim()}
              className="btn-red mt-3 text-sm"
              data-testid="bulk-import-btn"
            >
              <Plus size={14} /> Import
            </button>
          </div>
        </section>

        <section className="card-tech p-6" data-testid="admin-travel-section">
          <div className="flex items-center gap-3 mb-2">
            <Plane size={22} className="text-[#E6FF00]" />
            <h2 className="font-display font-bold text-2xl">Travel Partners (Airlines & Hotels)</h2>
          </div>
          <p className="text-sm text-[#71717a] mb-6">
            Edit deal text (e.g. "Save upto 20%"), taglines, links & logos. Each partner appears on the landing page in the corresponding grid and marquee.
          </p>

          {[
            { key: "airline", label: "Airlines", icon: Plane },
            { key: "hotel", label: "Hotels & Stays", icon: BedDouble },
          ].map((cat) => {
            const items = travelPartners.filter((p) => p.category === cat.key).sort((a, b) => a.sort_order - b.sort_order);
            return (
              <div key={cat.key} className="mb-8" data-testid={`admin-travel-${cat.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <cat.icon size={18} className="text-[#a1a1aa]" />
                    <h3 className="font-display font-semibold text-lg">{cat.label}</h3>
                    <span className="text-xs font-mono text-[#71717a]">({items.length})</span>
                  </div>
                  <button
                    onClick={() => setNewTravelPartner({ ...newTravelPartner, category: cat.key })}
                    className={`text-xs px-3 py-1.5 rounded-md font-mono transition ${
                      newTravelPartner.category === cat.key
                        ? "bg-[#E6FF00] text-black"
                        : "bg-white/5 text-[#a1a1aa] hover:bg-white/10"
                    }`}
                    data-testid={`select-travel-cat-${cat.key}`}
                  >
                    + Add {cat.label}
                  </button>
                </div>
                <div className="space-y-2">
                  {items.length === 0 ? (
                    <p className="text-xs text-[#71717a] italic">No partners yet.</p>
                  ) : (
                    items.map((p) => (
                      <div key={p.id} className="p-3 border border-white/10 rounded-lg" data-testid={`admin-travel-item-${p.id}`}>
                        {editingTravel?.id === p.id ? (
                          <div className="space-y-2">
                            <div className="grid md:grid-cols-2 gap-2">
                              <input value={editingTravel.name} onChange={(e) => setEditingTravel({ ...editingTravel, name: e.target.value })} placeholder="Name" className="input-dark text-sm" data-testid={`edit-travel-name-${p.id}`} />
                              <input value={editingTravel.target_url} onChange={(e) => setEditingTravel({ ...editingTravel, target_url: e.target.value })} placeholder="Link URL" className="input-dark text-sm" data-testid={`edit-travel-url-${p.id}`} />
                              <input value={editingTravel.logo_url} onChange={(e) => setEditingTravel({ ...editingTravel, logo_url: e.target.value })} placeholder="Logo URL" className="input-dark text-sm md:col-span-2" data-testid={`edit-travel-logo-${p.id}`} />
                              <input value={editingTravel.deal} onChange={(e) => setEditingTravel({ ...editingTravel, deal: e.target.value })} placeholder='Deal text (e.g. "Save upto 20%")' className="input-dark text-sm" data-testid={`edit-travel-deal-${p.id}`} />
                              <input type="number" value={editingTravel.sort_order} onChange={(e) => setEditingTravel({ ...editingTravel, sort_order: parseInt(e.target.value, 10) || 0 })} placeholder="Sort order" className="input-dark text-sm" data-testid={`edit-travel-sort-${p.id}`} />
                              <input value={editingTravel.tagline} onChange={(e) => setEditingTravel({ ...editingTravel, tagline: e.target.value })} placeholder="Tagline (shown in spotlight)" className="input-dark text-sm md:col-span-2" data-testid={`edit-travel-tagline-${p.id}`} />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={saveTravelEdit} className="btn-yellow text-xs px-3 py-1.5" data-testid={`save-travel-${p.id}`}>
                                <Save size={12} /> Save
                              </button>
                              <button onClick={() => setEditingTravel(null)} className="btn-ghost text-xs px-3 py-1.5" data-testid={`cancel-travel-${p.id}`}>
                                <X size={12} /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            {p.logo_url ? (
                              <img src={safeImageUrl(p.logo_url)} alt="" className="w-14 h-10 bg-white rounded object-contain p-1" />
                            ) : (
                              <div className="w-14 h-10 bg-white/5 rounded flex items-center justify-center text-[10px] text-[#71717a]">no logo</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-sm font-medium truncate">{p.name}</span>
                                {p.deal && (
                                  <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-[#FF0000] text-white uppercase tracking-wider">{p.deal}</span>
                                )}
                                {p.featured && (
                                  <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full bg-[#E6FF00] text-black uppercase tracking-wider">★ Spotlight</span>
                                )}
                              </div>
                              {p.tagline && <div className="text-[11px] text-[#a1a1aa] truncate italic">{p.tagline}</div>}
                              {p.target_url && <div className="text-[10px] text-[#71717a] truncate">{p.target_url}</div>}
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await api.put(`/travel-partners/${p.id}`, { featured: !p.featured });
                                  toast.success(p.featured ? "Unpinned from spotlight" : "Pinned as weekly spotlight");
                                  await load();
                                } catch (e) {
                                  if (e?.response?.status === 401) onLogout();
                                  else toast.error("Failed to toggle spotlight");
                                }
                              }}
                              className={p.featured ? "text-[#E6FF00]" : "text-[#71717a] hover:text-[#E6FF00]"}
                              title={p.featured ? "Unpin from weekly spotlight" : "Pin as weekly spotlight"}
                              data-testid={`toggle-spotlight-${p.id}`}
                            >
                              {p.featured ? <Star size={14} fill="currentColor" /> : <StarOff size={14} />}
                            </button>
                            <button onClick={() => startEditTravel(p)} className="text-[#71717a] hover:text-[#E6FF00]" data-testid={`edit-travel-${p.id}`}>
                              <Edit3 size={14} />
                            </button>
                            <button onClick={() => removeTravelPartner(p.id)} className="text-[#71717a] hover:text-[#FF0000]" data-testid={`delete-travel-${p.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}

          <div className="border-t border-white/10 pt-6 mt-2">
            <h3 className="font-display font-semibold text-lg mb-1">
              Add new <span className="text-[#E6FF00]">{newTravelPartner.category === "airline" ? "Airline" : "Hotel / Stay"}</span>
            </h3>
            <p className="text-xs text-[#71717a] mb-4">Upload the logo via chat, then paste its URL below. Deal text will show as a red badge (e.g. "Save upto 20%").</p>
            <div className="grid md:grid-cols-2 gap-3">
              <input value={newTravelPartner.name} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, name: e.target.value })} placeholder="Partner name (e.g. Air India) *" className="input-dark" data-testid="new-travel-name" />
              <input value={newTravelPartner.target_url} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, target_url: e.target.value })} placeholder="Affiliate link URL *" className="input-dark" data-testid="new-travel-url" />
              <input value={newTravelPartner.logo_url} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, logo_url: e.target.value })} placeholder="Logo URL" className="input-dark md:col-span-2" data-testid="new-travel-logo" />
              <input value={newTravelPartner.deal} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, deal: e.target.value })} placeholder='Deal text (e.g. "Save upto 20%")' className="input-dark" data-testid="new-travel-deal" />
              <input type="number" value={newTravelPartner.sort_order} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, sort_order: parseInt(e.target.value, 10) || 0 })} placeholder="Sort order" className="input-dark" data-testid="new-travel-sort" />
              <input value={newTravelPartner.tagline} onChange={(e) => setNewTravelPartner({ ...newTravelPartner, tagline: e.target.value })} placeholder="Tagline (shown in weekly spotlight)" className="input-dark md:col-span-2" data-testid="new-travel-tagline" />
            </div>
            <button onClick={addTravelPartner} className="btn-yellow mt-4" data-testid="add-travel-btn">
              <Plus size={16} /> Add travel partner
            </button>
          </div>

          <div className="border-t border-white/10 pt-6 mt-8">
            <h3 className="font-display font-semibold text-lg mb-2 text-[#E6FF00]">Bulk import (paste JSON)</h3>
            <p className="text-xs text-[#71717a] mb-3">
              Paste a JSON array of airlines/hotels. Duplicates (same category + name) are skipped. Useful for migrating from preview → production.
            </p>
            <textarea
              value={travelBulkImport}
              onChange={(e) => setTravelBulkImport(e.target.value)}
              rows={5}
              className="input-dark resize-none font-mono text-xs"
              placeholder='[{"category":"airline","name":"Air India","logo_url":"https://...","target_url":"https://...","deal":"Save upto 20%","tagline":""}]'
              data-testid="travel-bulk-import-textarea"
            />
            <button
              onClick={handleTravelBulkImport}
              disabled={!travelBulkImport.trim()}
              className="btn-red mt-3 text-sm"
              data-testid="travel-bulk-import-btn"
            >
              <Plus size={14} /> Import
            </button>
          </div>
        </section>

        <section className="card-tech p-6" data-testid="admin-subscription-section">
          <div className="flex items-center gap-3 mb-6">
            <Crown size={22} className="text-[#E6FF00]" />
            <h2 className="font-display font-bold text-2xl">Subscription plans</h2>
          </div>

          <div className="space-y-3 mb-8">
            {plans.map((p) => (
              <div key={p.id} className="p-4 border border-white/10 rounded-lg space-y-3" data-testid={`admin-plan-${p.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    value={p.duration_label}
                    onChange={(e) => setPlans(plans.map((x) => (x.id === p.id ? { ...x, duration_label: e.target.value } : x)))}
                    className="input-dark flex-1 min-w-[140px]"
                    placeholder="Duration label"
                    data-testid={`plan-label-${p.id}`}
                  />
                  <input
                    type="number"
                    value={p.duration_days}
                    onChange={(e) => setPlans(plans.map((x) => (x.id === p.id ? { ...x, duration_days: parseInt(e.target.value, 10) || 0 } : x)))}
                    className="input-dark w-28"
                    placeholder="Days"
                    data-testid={`plan-days-${p.id}`}
                  />
                  <input
                    value={p.price}
                    onChange={(e) => setPlans(plans.map((x) => (x.id === p.id ? { ...x, price: e.target.value } : x)))}
                    className="input-dark w-32"
                    placeholder="Price"
                    data-testid={`plan-price-${p.id}`}
                  />
                  <button
                    onClick={() => updatePlan(p.id, { popular: !p.popular })}
                    className={`px-3 py-2 rounded-md text-xs font-bold ${p.popular ? "bg-[#E6FF00] text-black" : "bg-white/5 text-[#71717a]"}`}
                    title="Mark as popular"
                    data-testid={`plan-popular-${p.id}`}
                  >
                    {p.popular ? <Star size={14} className="inline" /> : <StarOff size={14} className="inline" />}
                  </button>
                  <button
                    onClick={() => updatePlan(p.id, { active: !p.active })}
                    className={`px-3 py-2 rounded-md text-xs font-mono ${p.active ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"}`}
                    title={p.active ? "Active — click to disable" : "Inactive — click to enable"}
                    data-testid={`plan-active-${p.id}`}
                  >
                    {p.active ? "ACTIVE" : "OFF"}
                  </button>
                </div>
                <textarea
                  value={(p.features || []).join("\n")}
                  onChange={(e) => setPlans(plans.map((x) => (x.id === p.id ? { ...x, features: e.target.value.split("\n") } : x)))}
                  rows={3}
                  className="input-dark resize-none text-sm"
                  placeholder="One feature per line"
                  data-testid={`plan-features-${p.id}`}
                />
                <div className="flex justify-between items-center">
                  <button
                    onClick={() =>
                      updatePlan(p.id, {
                        duration_label: p.duration_label,
                        duration_days: p.duration_days,
                        price: p.price,
                        features: (p.features || []).map((f) => f.trim()).filter(Boolean),
                      })
                    }
                    className="btn-red text-sm"
                    data-testid={`plan-save-${p.id}`}
                  >
                    <Save size={14} /> Save
                  </button>
                  <button onClick={() => removePlan(p.id)} className="text-[#71717a] hover:text-[#FF0000] text-sm flex items-center gap-1.5" data-testid={`plan-delete-${p.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <h3 className="font-display font-semibold text-lg mb-4">Add new plan</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input value={newPlan.duration_label} onChange={(e) => setNewPlan({ ...newPlan, duration_label: e.target.value })} placeholder="Duration label (e.g. Lifetime)" className="input-dark" data-testid="new-plan-label" />
            <input type="number" value={newPlan.duration_days} onChange={(e) => setNewPlan({ ...newPlan, duration_days: parseInt(e.target.value, 10) || 0 })} placeholder="Duration in days" className="input-dark" data-testid="new-plan-days" />
            <input value={newPlan.price} onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })} placeholder="Price (e.g. ₹299)" className="input-dark" data-testid="new-plan-price" />
            <input type="number" value={newPlan.sort_order} onChange={(e) => setNewPlan({ ...newPlan, sort_order: parseInt(e.target.value, 10) || 0 })} placeholder="Sort order" className="input-dark" data-testid="new-plan-sort" />
            <textarea
              value={newPlan.features}
              onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })}
              placeholder="Features (one per line)"
              rows={3}
              className="input-dark resize-none md:col-span-2"
              data-testid="new-plan-features"
            />
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2 text-sm text-[#a1a1aa]">
              <input type="checkbox" checked={newPlan.popular} onChange={(e) => setNewPlan({ ...newPlan, popular: e.target.checked })} data-testid="new-plan-popular" /> Mark as popular
            </label>
          </div>
          <button onClick={addPlan} className="btn-yellow mt-4" data-testid="add-plan-btn">
            <Plus size={16} /> Add plan
          </button>
        </section>

        <section className="card-tech p-6" data-testid="admin-inquiries-section">
          <div className="flex items-center gap-3 mb-6">
            <Inbox size={22} className="text-[#E6FF00]" />
            <h2 className="font-display font-bold text-2xl">Subscription inquiries</h2>
            <span className="ml-auto text-xs font-mono text-[#71717a]" data-testid="inquiries-count">
              {inquiries.length} total · {inquiries.filter((i) => i.status === "new").length} new
            </span>
          </div>

          {inquiries.length === 0 ? (
            <p className="text-sm text-[#71717a] text-center py-8" data-testid="inquiries-empty">
              No inquiries yet. When a visitor clicks &quot;Choose Plan&quot;, their details will appear here.
            </p>
          ) : (
            <div className="space-y-3">
              {inquiries.map((inq) => (
                <div key={inq.id} className="p-4 border border-white/10 rounded-lg" data-testid={`inquiry-${inq.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-semibold text-lg">{inq.name}</div>
                      <div className="text-xs text-[#71717a] font-mono mt-0.5">
                        {new Date(inq.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-sm ${
                        inq.status === "new" ? "bg-[#FF0000] text-white" :
                        inq.status === "contacted" ? "bg-[#E6FF00] text-black" :
                        inq.status === "converted" ? "bg-emerald-500 text-black" :
                        "bg-zinc-700 text-zinc-300"
                      }`}>
                        {inq.status.toUpperCase()}
                      </span>
                      {inq.plan_label && (
                        <span className="text-xs font-mono bg-white/5 text-[#E6FF00] px-2 py-1 rounded-sm">
                          {inq.plan_label} · {inq.plan_price}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 text-sm text-[#e5e5e5] mb-3">
                    <a href={`mailto:${inq.email}`} className="flex items-center gap-2 hover:text-[#E6FF00]" data-testid={`inquiry-email-${inq.id}`}>
                      <Mail size={14} className="text-[#71717a]" /> {inq.email}
                    </a>
                    {inq.phone && (
                      <a href={`tel:${inq.phone}`} className="flex items-center gap-2 hover:text-[#E6FF00]">
                        <Phone size={14} className="text-[#71717a]" /> {inq.phone}
                      </a>
                    )}
                  </div>

                  {inq.message && (
                    <div className="text-sm text-[#a1a1aa] bg-white/5 rounded-md px-3 py-2 mb-3 italic">
                      &ldquo;{inq.message}&rdquo;
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {["new", "contacted", "converted", "closed"].map((st) => (
                      <button
                        key={st}
                        onClick={() => updateInquiryStatus(inq.id, st)}
                        disabled={inq.status === st}
                        className={`px-3 py-1 rounded text-xs font-mono ${
                          inq.status === st
                            ? "bg-white/10 text-white cursor-default"
                            : "bg-white/5 text-[#a1a1aa] hover:bg-white/10"
                        }`}
                        data-testid={`inquiry-status-${st}-${inq.id}`}
                      >
                        {st}
                      </button>
                    ))}
                    <button
                      onClick={() => removeInquiry(inq.id)}
                      className="ml-auto text-[#71717a] hover:text-[#FF0000] text-sm flex items-center gap-1.5"
                      data-testid={`delete-inquiry-${inq.id}`}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/admin/verify")
      .then(() => setAuthed(true))
      .catch(() => localStorage.removeItem("admin_token"))
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-white" size={28} />
      </div>
    );
  }

  if (!authed) return <LoginScreen onAuthed={() => setAuthed(true)} />;
  return <AdminPanel onLogout={handleLogout} />;
}
